import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
    Loader2, LogOut, ArrowLeft, X, Phone, Mail, Plus, Trash2, LayoutDashboard,
    Kanban, CalendarDays, Trophy, BellRing, BellOff, Building2, User as UserIcon,
} from 'lucide-react';
import { Wordmark } from '../components/Logo';
import { Login, apiFetch } from '../components/AdminPanel';

const API = process.env.REACT_APP_BACKEND_URL;

// ==================================================================
// MARCADORES DE MARCA — completa aquí cuando quieras
// - Logo: se renderiza con el monograma actual (Wordmark). Para cambiarlo:
//   edita /app/frontend/src/components/Logo.jsx
// - Colores: la CRM usa los tokens de la web (crimson, ink, pastelpink,
//   pastelblue, pastelcream). Cámbialos en tailwind.config.js y en las
//   variables CSS de src/index.css para repintar todo de una vez.
// ==================================================================
const BRAND = {
    nombre: '#PICHONTEAM Growth Partners',
    colorPrimario: '#9B1C2E', // MARCADOR: color primario de marca
    colorTinta: '#1B2A4A',    // MARCADOR: color oscuro de marca
    colorAcento: '#FCE7F0',   // MARCADOR: color acento de marca
};

const ETAPAS = [
    { id: 'nuevo', label: 'Lead nuevo' },
    { id: 'cita_agendada', label: 'Cita agendada' },
    { id: 'propuesta_enviada', label: 'Propuesta enviada' },
    { id: 'ganado', label: 'Ganado' },
    { id: 'perdido', label: 'Perdido' },
];

const ETIQUETA_SERVICIO = {
    branding: 'Branding', digitalizacion: 'Digitalización', automatizacion: 'Automatización',
    seo: 'SEO', sem: 'SEM', growth: 'Growth',
};

const casosConServicios = (c) => {
    const s = (c.servicio || '').toLowerCase();
    const tags = [];
    if (s.includes('branding')) tags.push('branding');
    if (s.includes('digitaliz')) tags.push('digitalizacion');
    if (s.includes('automatiz')) tags.push('automatizacion');
    if (s.includes('seo') || s.includes('posicionamiento')) tags.push('seo');
    if (s.includes('sem') || s.includes('ads')) tags.push('sem');
    if (s.includes('growth')) tags.push('growth');
    return { ...c, servicios: c.servicios?.length ? c.servicios : tags };
};

const fmtFecha = (d) => {
    if (!d) return 'Por confirmar';
    try {
        return new Date(`${d}T12:00:00`).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });
    } catch (e) { return d; }
};

const hoyStr = () => new Date().toISOString().split('T')[0];

async function listarCitas() {
    const res = await apiFetch(`${API}/api/appointments`);
    if (res.status === 401) throw new Error('unauthorized');
    if (!res.ok) throw new Error('No se pudieron cargar los leads');
    return res.json();
}

/* ---------- Panel ---------- */
function StatCard({ label, value, testid, accent, icon: Icon }) {
    return (
        <div className={`rounded-2xl border p-5 ${accent ? 'bg-ink text-pastelcream border-ink' : 'bg-white border-ink/10'}`} data-testid={testid}>
            <div className="flex items-center justify-between">
                <div className={`font-display text-3xl font-semibold ${accent ? 'text-pastelcream' : 'text-ink'}`}>{value}</div>
                {Icon && <Icon size={20} className={accent ? 'text-pastelpink' : 'text-crimson'} />}
            </div>
            <div className={`text-[11px] uppercase tracking-[0.16em] font-semibold mt-1 ${accent ? 'text-pastelcream/60' : 'text-ink/50'}`}>{label}</div>
        </div>
    );
}

function PanelView({ summary, goTab }) {
    if (!summary) return null;
    return (
        <div data-testid="crm-panel">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <StatCard accent icon={UserIcon} label="Leads nuevos" value={summary.leads_nuevos} testid="crm-stat-leads-nuevos" />
                <StatCard icon={CalendarDays} label="Citas de la semana" value={summary.citas_semana} testid="crm-stat-citas-semana" />
                <StatCard icon={Kanban} label="Propuestas abiertas" value={summary.propuestas_abiertas} testid="crm-stat-propuestas" />
            </div>
            <div className="grid grid-cols-3 gap-4 mt-4">
                <StatCard icon={Trophy} label="Ganados" value={summary.ganados} testid="crm-stat-ganados" />
                <StatCard label="Perdidos" value={summary.perdidos} testid="crm-stat-perdidos" />
                <StatCard label="Total leads" value={summary.total} testid="crm-stat-total" />
            </div>

            <div className="mt-8 bg-white rounded-2xl border border-ink/10 p-6" data-testid="crm-week-list">
                <div className="flex items-center justify-between">
                    <h3 className="font-display text-lg font-semibold text-ink">Citas de esta semana</h3>
                    <button onClick={() => goTab('calendario')} className="text-[11px] font-bold uppercase tracking-[0.14em] text-crimson hover:underline" data-testid="crm-week-see-calendar">
                        Ver calendario
                    </button>
                </div>
                {(summary.citas_semana_lista || []).length === 0 ? (
                    <p className="mt-4 text-sm text-ink/50" data-testid="crm-week-empty">
                        No hay citas para esta semana. Cuando lleguen solicitudes con fecha, aparecerán aquí.
                    </p>
                ) : (
                    <ul className="mt-4 space-y-2">
                        {summary.citas_semana_lista.map((c, i) => (
                            <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-canvas border border-ink/10 px-4 py-3" data-testid={`crm-week-item-${i + 1}`}>
                                <span className="text-sm font-semibold text-ink">{c.nombre} · <span className="font-normal text-ink/60">{c.empresa}</span></span>
                                <span className="text-xs text-ink/60">{fmtFecha(c.fecha_preferida)} — {c.servicio}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

/* ---------- Embudo ---------- */
function LeadCard({ a, i, onOpen, onAdvance }) {
    const etapaIdx = ETAPAS.findIndex((e) => e.id === (a.etapa || 'nuevo'));
    const done = a.etapa === 'ganado' || a.etapa === 'perdido';
    return (
        <div className="bg-white rounded-xl border border-ink/10 p-4 shadow-sm" data-testid={`crm-lead-card-${i + 1}`}>
            <button onClick={() => onOpen(a)} className="w-full text-left" data-testid={`crm-lead-open-${i + 1}`}>
                <p className="font-display font-semibold text-ink text-[15px] leading-snug">{a.nombre}</p>
                <p className="text-xs text-ink/55 mt-0.5 flex items-center gap-1"><Building2 size={11} /> {a.empresa || '—'}</p>
                <div className="flex flex-wrap gap-1.5 mt-2">
                    <span className="rounded-full bg-pastelpink/70 border border-crimson/20 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] text-crimson">{a.servicio}</span>
                    <span className="rounded-full bg-pastelblue/70 border border-ink/10 px-2.5 py-0.5 text-[10px] font-bold tracking-[0.06em] text-ink/70">{fmtFecha(a.fecha_preferida)}</span>
                </div>
            </button>
            {!done && etapaIdx < ETAPAS.length - 2 && (
                <button
                    onClick={() => onAdvance(a, ETAPAS[etapaIdx + 1].id)}
                    className="mt-3 text-[10px] font-bold uppercase tracking-[0.12em] text-ink/50 hover:text-crimson transition-colors"
                    data-testid={`crm-lead-advance-${i + 1}`}
                >
                    Avanzar a {ETAPAS[etapaIdx + 1].label} →
                </button>
            )}
            {a.etapa === 'ganado' && <p className="mt-2 text-[10px] font-bold uppercase tracking-[0.12em] text-green-700">Ganado</p>}
            {a.etapa === 'perdido' && <p className="mt-2 text-[10px] font-bold uppercase tracking-[0.12em] text-crimson">Perdido</p>}
        </div>
    );
}

function PipelineView({ citas, onOpen, onAdvance }) {
    return (
        <div className="flex gap-4 overflow-x-auto pb-4" data-testid="crm-pipeline">
            {ETAPAS.map((e) => {
                const items = citas.filter((a) => (a.etapa || 'nuevo') === e.id);
                return (
                    <div key={e.id} className="min-w-[212px] flex-1" data-testid={`crm-pipeline-col-${e.id}`}>
                        <div className="flex items-center justify-between mb-3 px-1">
                            <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-ink/70">{e.label}</span>
                            <span className="rounded-full bg-ink text-pastelcream text-[10px] font-bold px-2 py-0.5" data-testid={`crm-pipeline-count-${e.id}`}>{items.length}</span>
                        </div>
                        <div className="space-y-3 rounded-2xl bg-white/60 border border-ink/5 p-3 min-h-[120px]">
                            {items.length === 0 && (
                                <p className="text-xs text-ink/40 text-center pt-6">Sin leads en esta etapa</p>
                            )}
                            {items.map((a) => {
                                const gi = citas.indexOf(a);
                                return <LeadCard key={a._id} a={a} i={gi} onOpen={onOpen} onAdvance={onAdvance} />;
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

/* ---------- Detalle de lead (modal) ---------- */
function LeadModal({ a, onClose, onUpdate }) {
    const [nota, setNota] = useState('');
    const [saving, setSaving] = useState(false);
    const [casos, setCasos] = useState([]);

    useEffect(() => {
        fetch(`${API}/api/cases`)
            .then((r) => (r.ok ? r.json() : []))
            .then((data) => setCasos((Array.isArray(data) ? data : []).map(casosConServicios)))
            .catch(() => {});
    }, []);

    const tagsLead = casosConServicios({ servicio: a.servicio }).servicios;
    const relacionados = casos.filter((c) => c.servicios.some((t) => tagsLead.includes(t)));

    const setEtapa = async (etapa) => {
        if (saving) return;
        setSaving(true);
        const res = await apiFetch(`${API}/api/appointments/${a._id}/etapa`, {
            method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ etapa }),
        });
        setSaving(false);
        if (!res.ok) return toast.error('No se pudo cambiar la etapa');
        toast.success(`Etapa: ${ETAPAS.find((e) => e.id === etapa)?.label}`);
        onUpdate(await res.json());
    };

    const addNota = async (e) => {
        e.preventDefault();
        if (!nota.trim() || saving) return;
        setSaving(true);
        const res = await apiFetch(`${API}/api/appointments/${a._id}/notas`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ texto: nota }),
        });
        setSaving(false);
        if (!res.ok) return toast.error('No se pudo guardar la nota');
        setNota('');
        toast.success('Nota guardada');
        onUpdate(await res.json());
    };

    const delNota = async (notaId) => {
        const res = await apiFetch(`${API}/api/appointments/${a._id}/notas/${notaId}`, { method: 'DELETE' });
        if (!res.ok) return toast.error('No se pudo borrar la nota');
        onUpdate(await res.json());
    };

    return (
        <div className="fixed inset-0 z-[80] bg-ink/50 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="crm-modal-overlay">
            <div className="w-full max-w-2xl rounded-[1.4rem] bg-white p-7 lg:p-8 relative my-8" onClick={(e) => e.stopPropagation()} data-testid="crm-modal">
                <button onClick={onClose} aria-label="Cerrar" className="absolute top-4 right-4 w-9 h-9 rounded-full border border-ink/15 flex items-center justify-center text-ink/60 hover:text-crimson hover:border-crimson transition-colors" data-testid="crm-modal-close">
                    <X size={16} />
                </button>

                <p className="font-ink text-[10px] uppercase tracking-[0.3em] text-ink/50">{fmtFecha(a.fecha_preferida)}</p>
                <h3 className="font-display text-2xl font-semibold text-ink mt-1">{a.nombre}</h3>
                <p className="text-sm text-ink/60 mt-1">{a.empresa}</p>

                <div className="flex flex-wrap gap-2 mt-4">
                    <span className="rounded-full bg-pastelpink/70 border border-crimson/20 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-crimson">{a.servicio}</span>
                    <span className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] border ${a.status === 'confirmada' ? 'bg-green-100 text-green-800 border-green-300' : 'bg-pastelcream text-ink border-ink/20'}`}>{a.status}</span>
                </div>

                <div className="flex flex-wrap gap-3 mt-5">
                    <a href={`mailto:${a.email}`} className="inline-flex items-center gap-1.5 text-sm text-crimson font-semibold hover:underline" data-testid="crm-lead-email"><Mail size={14} /> {a.email}</a>
                    <a href={`tel:${a.telefono}`} className="inline-flex items-center gap-1.5 text-sm text-crimson font-semibold hover:underline" data-testid="crm-lead-phone"><Phone size={14} /> {a.telefono}</a>
                </div>
                {a.mensaje && <p className="mt-4 text-sm text-ink/70 leading-relaxed bg-canvas rounded-xl border border-ink/10 p-4">{a.mensaje}</p>}

                <h4 className="text-[11px] font-bold uppercase tracking-[0.16em] text-ink/60 mt-6">Etapa del embudo</h4>
                <div className="flex flex-wrap gap-2 mt-3" data-testid="crm-etapa-selector">
                    {ETAPAS.map((e) => (
                        <button
                            key={e.id}
                            onClick={() => setEtapa(e.id)}
                            className={`rounded-full px-3.5 py-2 text-[11px] font-bold uppercase tracking-[0.1em] border transition-all ${(a.etapa || 'nuevo') === e.id ? 'bg-ink text-pastelcream border-ink' : 'bg-white text-ink/60 border-ink/15 hover:border-crimson hover:text-crimson'}`}
                            data-testid={`crm-etapa-${e.id}`}
                        >
                            {e.label}
                        </button>
                    ))}
                </div>

                <h4 className="text-[11px] font-bold uppercase tracking-[0.16em] text-ink/60 mt-6">Notas de la reunión</h4>
                <form onSubmit={addNota} className="flex gap-2 mt-3">
                    <input
                        value={nota}
                        onChange={(e) => setNota(e.target.value)}
                        placeholder="Anotación de la reunión con este cliente..."
                        className="flex-1 rounded-xl border border-ink/15 px-4 py-2.5 text-sm focus:border-crimson focus:outline-none"
                        data-testid="crm-nota-input"
                    />
                    <button type="submit" disabled={saving} className="rounded-xl bg-ink text-pastelcream px-4 text-[11px] font-bold uppercase tracking-[0.12em] hover:bg-crimson transition-colors disabled:opacity-60" data-testid="crm-nota-add">
                        <Plus size={15} />
                    </button>
                </form>
                {(a.notas || []).length === 0 ? (
                    <p className="mt-3 text-xs text-ink/45" data-testid="crm-notas-empty">Sin notas todavía. Añade aquí lo que habléis en cada reunión.</p>
                ) : (
                    <ul className="mt-3 space-y-2">
                        {a.notas.map((n, i) => (
                            <li key={n.id} className="flex items-start justify-between gap-3 rounded-xl bg-canvas border border-ink/10 px-4 py-2.5" data-testid={`crm-nota-item-${i + 1}`}>
                                <div>
                                    <p className="text-sm text-ink">{n.texto}</p>
                                    <p className="text-[10px] text-ink/45 mt-0.5">{new Date(n.created_at).toLocaleString('es-ES')}</p>
                                </div>
                                <button onClick={() => delNota(n.id)} className="text-ink/40 hover:text-crimson transition-colors" data-testid={`crm-nota-del-${i + 1}`} aria-label="Borrar nota">
                                    <Trash2 size={14} />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}

                <h4 className="text-[11px] font-bold uppercase tracking-[0.16em] text-ink/60 mt-6" data-testid="crm-casos-relacionados">Casos de éxito relacionados</h4>
                {relacionados.length === 0 ? (
                    <p className="mt-3 text-xs text-ink/45" data-testid="crm-casos-rel-empty">No hay casos vinculados a este servicio todavía.</p>
                ) : (
                    <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {relacionados.map((c, i) => (
                            <li key={c._id} className="rounded-xl border border-ink/10 bg-white p-3" data-testid={`crm-caso-relacionado-${i + 1}`}>
                                <p className="font-display text-sm font-semibold text-ink">{c.cliente}</p>
                                <p className="text-[11px] text-ink/55 mt-0.5">{c.sector} · {c.resultado}</p>
                                <div className="flex flex-wrap gap-1 mt-2">
                                    {c.servicios.map((t) => <span key={t} className="rounded-full bg-pastelblue/60 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.08em] text-ink/70">{ETIQUETA_SERVICIO[t] || t}</span>)}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

/* ---------- Calendario ---------- */
function CitaCalendario({ a, i, pasada, onUpdate }) {
    const [nota, setNota] = useState('');
    const [open, setOpen] = useState(false);
    const recordatorio = a.reminder_sent_at
        ? { txt: 'Recordatorio enviado', icon: BellRing, cls: 'text-green-700 bg-green-100 border-green-300' }
        : { txt: 'Recordatorio: día antes, 9:00', icon: BellOff, cls: 'text-ink/60 bg-pastelcream border-ink/15' };
    const Icon = recordatorio.icon;

    const addNota = async (e) => {
        e.preventDefault();
        if (!nota.trim()) return;
        const res = await apiFetch(`${API}/api/appointments/${a._id}/notas`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ texto: nota }),
        });
        if (!res.ok) return toast.error('No se pudo guardar la nota');
        setNota('');
        toast.success('Nota guardada');
        onUpdate(await res.json());
    };

    return (
        <div className={`rounded-2xl border p-5 ${pasada ? 'bg-canvas border-ink/10 opacity-70' : 'bg-white border-ink/10'}`} data-testid={`crm-cita-item-${i + 1}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                    <p className="font-display font-semibold text-ink">{a.nombre} · <span className="font-normal text-ink/60 text-sm">{a.empresa}</span></p>
                    <p className="text-xs text-ink/55 mt-0.5">{fmtFecha(a.fecha_preferida)} — {a.servicio} — {a.email}</p>
                </div>
                <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.08em] ${recordatorio.cls}`} data-testid={`crm-recordatorio-badge-${i + 1}`}>
                    <Icon size={11} /> {recordatorio.txt}
                </span>
            </div>
            <button onClick={() => setOpen(!open)} className="mt-3 text-[10px] font-bold uppercase tracking-[0.12em] text-crimson hover:underline" data-testid={`crm-cita-notas-toggle-${i + 1}`}>
                {open ? 'Ocultar notas' : `Notas (${(a.notas || []).length})`}
            </button>
            {open && (
                <div className="mt-3">
                    {(a.notas || []).map((n, j) => (
                        <div key={n.id} className="flex items-start justify-between gap-3 rounded-xl bg-canvas border border-ink/10 px-4 py-2.5 mb-2" data-testid={`crm-cita-nota-${i + 1}-${j + 1}`}>
                            <div>
                                <p className="text-sm text-ink">{n.texto}</p>
                                <p className="text-[10px] text-ink/45 mt-0.5">{new Date(n.created_at).toLocaleString('es-ES')}</p>
                            </div>
                            <button
                                onClick={async () => {
                                    const res = await apiFetch(`${API}/api/appointments/${a._id}/notas/${n.id}`, { method: 'DELETE' });
                                    if (res.ok) onUpdate(await res.json());
                                }}
                                className="text-ink/40 hover:text-crimson transition-colors" aria-label="Borrar nota" data-testid={`crm-cita-nota-del-${i + 1}-${j + 1}`}
                            >
                                <Trash2 size={13} />
                            </button>
                        </div>
                    ))}
                    <form onSubmit={addNota} className="flex gap-2">
                        <input value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Nota de la reunión..." className="flex-1 rounded-xl border border-ink/15 px-4 py-2 text-sm focus:border-crimson focus:outline-none" data-testid={`crm-cita-nota-input-${i + 1}`} />
                        <button type="submit" className="rounded-xl bg-ink text-pastelcream px-4 text-[11px] font-bold uppercase tracking-[0.12em] hover:bg-crimson transition-colors" data-testid={`crm-cita-nota-save-${i + 1}`}>Guardar</button>
                    </form>
                </div>
            )}
        </div>
    );
}

function CalendarView({ citas, onUpdate }) {
    const hoy = hoyStr();
    const conFecha = citas.filter((a) => a.fecha_preferida).sort((x, y) => x.fecha_preferida.localeCompare(y.fecha_preferida));
    const proximas = conFecha.filter((a) => a.fecha_preferida >= hoy);
    const pasadas = conFecha.filter((a) => a.fecha_preferida < hoy);
    const sinFecha = citas.filter((a) => !a.fecha_preferida);

    return (
        <div data-testid="crm-calendar">
            <h3 className="font-display text-lg font-semibold text-ink">Próximas citas</h3>
            {proximas.length === 0 ? (
                <p className="mt-3 text-sm text-ink/50 bg-white rounded-2xl border border-dashed border-ink/20 p-6 text-center" data-testid="crm-calendar-empty">
                    No hay citas próximas. Las solicitudes con fecha del formulario web aparecerán aquí ordenadas por día.
                </p>
            ) : (
                <div className="mt-4 space-y-3">
                    {proximas.map((a, i) => <CitaCalendario key={a._id} a={a} i={i} pasada={false} onUpdate={onUpdate} />)}
                </div>
            )}

            {pasadas.length > 0 && (
                <>
                    <h3 className="font-display text-lg font-semibold text-ink mt-8">Citas pasadas</h3>
                    <div className="mt-4 space-y-3">
                        {pasadas.map((a, i) => <CitaCalendario key={a._id} a={a} i={proximas.length + i} pasada onUpdate={onUpdate} />)}
                    </div>
                </>
            )}

            {sinFecha.length > 0 && (
                <>
                    <h3 className="font-display text-lg font-semibold text-ink mt-8">Sin fecha confirmada</h3>
                    <div className="mt-4 space-y-3">
                        {sinFecha.map((a, i) => <CitaCalendario key={a._id} a={a} i={proximas.length + pasadas.length + i} pasada={false} onUpdate={onUpdate} />)}
                    </div>
                </>
            )}
        </div>
    );
}

/* ---------- Casos de éxito ---------- */
function CasosView({ casos }) {
    const [filtro, setFiltro] = useState('todos');
    const visibles = filtro === 'todos' ? casos : casos.filter((c) => c.servicios.includes(filtro));
    return (
        <div data-testid="crm-casos">
            <div className="flex flex-wrap gap-2" data-testid="crm-casos-filters">
                {[{ id: 'todos', label: 'Todos' }, ...Object.entries(ETIQUETA_SERVICIO).map(([id, label]) => ({ id, label }))].map((f) => (
                    <button
                        key={f.id}
                        onClick={() => setFiltro(f.id)}
                        className={`rounded-full px-4 py-2 text-[11px] font-bold uppercase tracking-[0.12em] border transition-all ${filtro === f.id ? 'bg-ink text-pastelcream border-ink' : 'bg-white text-ink/60 border-ink/15 hover:border-crimson hover:text-crimson'}`}
                        data-testid={`crm-casos-filter-${f.id}`}
                    >
                        {f.label}
                    </button>
                ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
                {visibles.length === 0 && (
                    <p className="text-sm text-ink/50 bg-white rounded-2xl border border-dashed border-ink/20 p-6 text-center col-span-full" data-testid="crm-casos-empty">
                        No hay casos de éxito vinculados a este servicio todavía.
                    </p>
                )}
                {visibles.map((c, i) => (
                    <div key={c._id} className="bg-white rounded-2xl border border-ink/10 p-5" data-testid={`crm-caso-card-${i + 1}`}>
                        <div className="flex flex-wrap gap-1" data-testid={`crm-caso-tags-${i + 1}`}>
                            {c.servicios.map((t) => <span key={t} className="rounded-full bg-pastelpink/70 border border-crimson/20 px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.08em] text-crimson">{ETIQUETA_SERVICIO[t] || t}</span>)}
                        </div>
                        <p className="font-display text-lg font-semibold text-ink mt-3">{c.cliente}</p>
                        <p className="text-xs text-ink/55">{c.sector}</p>
                        <p className="text-sm text-ink/70 mt-2 leading-relaxed">{c.resultado}</p>
                        <p className="text-[11px] text-ink/45 mt-2">{c.servicio}</p>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ---------- Shell ---------- */
const TABS = [
    { id: 'panel', label: 'Panel', icon: LayoutDashboard },
    { id: 'embudo', label: 'Embudo', icon: Kanban },
    { id: 'calendario', label: 'Calendario', icon: CalendarDays },
    { id: 'casos', label: 'Casos de éxito', icon: Trophy },
];

function CrmShell({ user, onLogout }) {
    const [tab, setTab] = useState('panel');
    const [citas, setCitas] = useState(null);
    const [summary, setSummary] = useState(null);
    const [casos, setCasos] = useState([]);
    const [lead, setLead] = useState(null);

    const load = async () => {
        try {
            const [r1, r2, r3] = await Promise.all([
                apiFetch(`${API}/api/appointments`),
                apiFetch(`${API}/api/crm/summary`),
                apiFetch(`${API}/api/cases`),
            ]);
            if (r1.status === 401 || r2.status === 401) return onLogout();
            if (r1.ok) setCitas(await r1.json());
            if (r2.ok) setSummary(await r2.json());
            if (r3.ok) setCasos((await r3.json()).map(casosConServicios));
        } catch (e) {
            toast.error('No se pudo cargar el CRM');
        }
    };

    useEffect(() => { load(); }, []);

    const onUpdate = (updated) => {
        setCitas((cs) => (cs || []).map((c) => (c._id === updated._id ? updated : c)));
        setLead((l) => (l && l._id === updated._id ? updated : l));
        apiFetch(`${API}/api/crm/summary`).then(async (r) => { if (r.ok) setSummary(await r.json()); });
    };

    const onAdvance = async (a, etapa) => {
        const res = await apiFetch(`${API}/api/appointments/${a._id}/etapa`, {
            method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ etapa }),
        });
        if (!res.ok) return toast.error('No se pudo avanzar la etapa');
        toast.success(`Etapa: ${ETAPAS.find((e) => e.id === etapa)?.label}`);
        onUpdate(await res.json());
    };

    return (
        <div className="min-h-screen bg-canvas" data-testid="crm-shell">
            <header className="bg-ink text-pastelcream">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        {/* MARCADOR: logo de marca — sustituir Wordmark en components/Logo.jsx */}
                        <Wordmark dark compact />
                        <span className="hidden sm:inline text-[11px] font-ink uppercase tracking-[0.2em] text-pastelcream/50 border-l border-pastelcream/20 pl-4">
                            CRM comercial
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="hidden md:inline text-xs text-pastelcream/50">{user?.email}</span>
                        <a href="/admin" className="hidden sm:inline-flex text-[11px] font-bold uppercase tracking-[0.14em] text-pastelcream/60 hover:text-pastelcream items-center gap-1.5" data-testid="crm-link-panel">
                            <CalendarDays size={13} /> Panel citas
                        </a>
                        <a href="/" className="text-[11px] font-bold uppercase tracking-[0.14em] text-pastelcream/60 hover:text-pastelcream inline-flex items-center gap-1.5" data-testid="crm-link-site">
                            <ArrowLeft size={13} /> Web
                        </a>
                        <button onClick={onLogout} className="inline-flex items-center gap-2 rounded-full border border-pastelcream/25 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.14em] hover:border-crimson hover:text-crimson transition-colors" data-testid="crm-logout">
                            <LogOut size={13} /> Salir
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 pb-16">
                <div className="flex flex-wrap gap-2" data-testid="crm-tabs">
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.12em] border transition-all ${tab === t.id ? 'bg-ink text-pastelcream border-ink' : 'bg-white text-ink/60 border-ink/15 hover:border-crimson hover:text-crimson'}`}
                            data-testid={`crm-tab-${t.id}`}
                        >
                            <t.icon size={13} /> {t.label}
                        </button>
                    ))}
                </div>

                {citas === null ? (
                    <div className="flex items-center gap-3 text-ink/60 p-10" data-testid="crm-loading">
                        <Loader2 size={18} className="animate-spin" /> Cargando CRM...
                    </div>
                ) : (
                    <div className="mt-8">
                        {tab === 'panel' && <PanelView summary={summary} goTab={setTab} />}
                        {tab === 'embudo' && <PipelineView citas={citas} onOpen={setLead} onAdvance={onAdvance} />}
                        {tab === 'calendario' && <CalendarView citas={citas} onUpdate={onUpdate} />}
                        {tab === 'casos' && <CasosView casos={casos} />}
                    </div>
                )}
            </main>

            {lead && <LeadModal a={lead} onClose={() => setLead(null)} onUpdate={onUpdate} />}
        </div>
    );
}

export default function CrmPage() {
    const [user, setUser] = useState(null);

    useEffect(() => {
        fetch(`${API}/api/auth/me`, { credentials: 'include' })
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error('no session'))))
            .then(setUser)
            .catch(() => setUser(false));
    }, []);

    if (user === null) {
        return (
            <div className="min-h-screen bg-ink flex items-center justify-center text-pastelcream" data-testid="crm-checking">
                <Loader2 size={22} className="animate-spin" />
            </div>
        );
    }

    if (user && user.role !== 'admin') {
        return (
            <div className="min-h-screen bg-ink flex items-center justify-center px-4" data-testid="crm-denied">
                <div className="bg-white rounded-[1.6rem] p-10 max-w-md text-center">
                    <h2 className="font-display text-2xl font-semibold text-ink">Acceso restringido</h2>
                    <p className="text-ink/65 text-sm mt-3">La cuenta <strong>{user.email}</strong> no tiene permisos para el CRM.</p>
                    <button
                        onClick={async () => { await fetch(`${API}/api/auth/logout`, { method: 'POST', credentials: 'include' }); setUser(false); }}
                        className="mt-8 rounded-full bg-ink px-6 py-3 text-[12px] font-bold uppercase tracking-[0.14em] text-pastelcream hover:bg-crimson transition-colors"
                        data-testid="crm-denied-logout"
                    >
                        Cerrar sesion
                    </button>
                </div>
            </div>
        );
    }

    return user ? <CrmShell user={user} onLogout={() => setUser(false)} /> : <Login onLogin={setUser} title="CRM comercial" />;
}
